import { useState, useEffect } from 'react';
import { client } from '@/api/client';
import { Printer, FileDown } from 'lucide-react';
import DateRangeFilter from './DateRangeFilter';
import { exportCSV, inRange } from './reportUtils';
import { useCompanyInfo, generatePrintHTML, openPrint } from './ReportExporter';

export default function LaneReport() {
  const [loads, setLoads] = useState([]);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [loading, setLoading] = useState(true);
  const company = useCompanyInfo();

  useEffect(() => {
    client.entities.Load.list('-delivery_date', 500).then(l => { setLoads(l); setLoading(false); });
  }, []);

  const filtered = loads.filter(l => inRange(l.delivery_date || l.pickup_date, start, end) && l.origin && l.destination);

  const byLane = {};
  filtered.forEach(l => {
    const lane = `${l.origin} → ${l.destination}`;
    if (!byLane[lane]) {
      byLane[lane] = { Lane: lane, 'Load Count': 0, 'Total Miles': 0, 'Gross Revenue': 0 };
    }
    byLane[lane]['Load Count'] += 1;
    byLane[lane]['Total Miles'] += l.miles || 0;
    byLane[lane]['Gross Revenue'] += l.rate || 0;
  });

  const rows = Object.values(byLane)
    .map(r => ({
      ...r,
      'Total Miles': Math.round(r['Total Miles']),
      'Avg Rate/Load': r['Load Count'] > 0 ? r['Gross Revenue'] / r['Load Count'] : 0,
      'Revenue/Mile': r['Total Miles'] > 0 ? r['Gross Revenue'] / r['Total Miles'] : 0,
    }))
    .sort((a, b) => b['Gross Revenue'] - a['Gross Revenue']);

  const csvRows = rows.map(r => ({
    ...r,
    'Gross Revenue': r['Gross Revenue'].toFixed(2),
    'Avg Rate/Load': r['Avg Rate/Load'].toFixed(2),
    'Revenue/Mile': r['Revenue/Mile'].toFixed(2),
  }));

  const handlePrint = () => {
    const cols = ['Lane', 'Load Count', 'Total Miles', 'Gross Revenue', 'Avg Rate/Load', 'Revenue/Mile'];
    const dataRows = rows.map(r => [
      { value: r.Lane, class: 'bold' },
      r['Load Count'],
      r['Total Miles'].toLocaleString(),
      { value: `$${r['Gross Revenue'].toLocaleString()}`, class: 'positive' },
      `$${r['Avg Rate/Load'].toFixed(2)}`,
      r['Total Miles'] > 0 ? { value: `$${r['Revenue/Mile'].toFixed(2)}`, class: 'gold' } : '—',
    ]);
    const grandGross = rows.reduce((s, r) => s + r['Gross Revenue'], 0);
    const grandLoads = rows.reduce((s, r) => s + r['Load Count'], 0);
    const totalsRow = ['TOTALS', grandLoads, '', { value: `$${grandGross.toLocaleString()}`, class: 'gold' }, '', ''];
    const cards = [
      { label: 'Lanes Run', value: rows.length, color: 'gold' },
      { label: 'Total Loads', value: grandLoads, color: '' },
      { label: 'Gross Revenue', value: `$${grandGross.toLocaleString()}`, color: 'positive' },
    ];
    const html = generatePrintHTML('Lane Report', `${start || 'All'} – ${end || 'All'}`, cols, dataRows, totalsRow, company, cards);
    openPrint(html);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <DateRangeFilter onChange={(s, e) => { setStart(s); setEnd(e); }} onExport={() => exportCSV(csvRows, 'lane-report.csv')} exportDisabled={loading || rows.length === 0} />
        <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground hover:bg-white/10"><Printer className="w-4 h-4" /> Print</button>
        <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground hover:bg-white/10"><FileDown className="w-4 h-4" /> Export PDF</button>
      </div>
      <div className="glass-card overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border">
              {['Lane', 'Load Count', 'Total Miles', 'Gross Revenue', 'Avg Rate/Load', 'Revenue/Mile'].map(h => (
                <th key={h} className="text-left px-3 py-2.5 text-muted-foreground font-medium whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map(r => (
              <tr key={r.Lane} className="hover:bg-white/3">
                <td className="px-3 py-2.5 font-semibold text-primary whitespace-nowrap">{r.Lane}</td>
                <td className="px-3 py-2.5 text-muted-foreground">{r['Load Count']}</td>
                <td className="px-3 py-2.5 text-muted-foreground">{r['Total Miles'].toLocaleString()}</td>
                <td className="px-3 py-2.5 font-semibold text-green-400">${r['Gross Revenue'].toLocaleString()}</td>
                <td className="px-3 py-2.5 text-foreground">${r['Avg Rate/Load'].toFixed(2)}</td>
                <td className="px-3 py-2.5 text-muted-foreground">{r['Total Miles'] > 0 ? `$${r['Revenue/Mile'].toFixed(2)}` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && !loading && <p className="text-center text-muted-foreground py-8 text-sm">No lane data for this date range yet.</p>}
        {loading && <p className="text-center text-muted-foreground py-8 text-sm">Loading...</p>}
      </div>
    </div>
  );
}
